import React, { useState } from 'react'
import { Pencil } from 'lib/lucide'

import { ERRORS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'

import { getStores } from './api'
import { DefaultTable } from '../components'
import { GetStoresResponse, Store, StoreWithTags } from './types'
import { CreateStoresDialog, DeleteStoreDialog, UpdateStoreDialog } from './components'

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Razón social', field: 'name' },
  { title: 'Nombre fantasía', field: 'secondaryName' },
  {
    title: 'Tags',
    field: 'tags',
    render: (rowData: StoreWithTags) =>
      rowData.tags && Array.isArray(rowData.tags)
        ? rowData.tags.map(tag => tag.title).join(', ')
        : 'Sin tags',
  },
  {
    title: 'USD',
    field: 'usdEnabled',
    render: (rowData: Store) => (rowData.usdEnabled ? 'Activo' : 'Inactivo'),
  },
  {
    title: 'Suma puntos',
    field: 'pointsEnabled',
    render: (rowData: Store) => (rowData.pointsEnabled ? 'Si' : 'No'),
  },
  { title: 'Estado', field: 'status' },
]
const getStoresResponseGetter = (responseData: GetStoresResponse): Store[] => {
  return (
    responseData?.stores?.map(store => {
      const storeWithTags = store as Store & { tags?: { id: number; title: string }[] }

      return {
        ...store,
        tags: Array.isArray(storeWithTags.tags) ? storeWithTags.tags : [],
        status: store.active ? 'Activo' : 'Inactivo',
        usdEnabled: !!store.usdEnabled,
        pointsEnabled: store.pointsEnabled !== false,
      }
    }) ?? []
  )
}

const Stores: React.FC = () => {
  const [{ data: stores, isLoading }, refetchStores] = useApi<
    GetStoresResponse,
    Store[]
  >(getStores, getStoresResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [storeToDelete, setStoreToDelete] = useState<Store | null>(null)
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
  const [storeToUpdate, setStoreToUpdate] = useState<Store | null>(null)

  const onCreateDone = async () => {
    setIsCreateDialogOpen(false)
    await refetchStores()
  }

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false)
    setStoreToDelete(null)
  }

  const onDeleteDone = async () => {
    closeDeleteDialog()
    await refetchStores()
  }

  const closeUpdateDialog = () => {
    setIsUpdateDialogOpen(false)
    setStoreToUpdate(null)
  }

  const onUpdateDone = async () => {
    closeUpdateDialog()
    await refetchStores()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva marca',
      isFreeAction: true,
      onClick: () => setIsCreateDialogOpen(true),
    },
    {
      icon: () => <Pencil size={20} />,
      tooltip: 'Editar marca',
      isFreeAction: false,
      onClick: (_: React.SyntheticEvent, store: Store | Store[]) => {
        setStoreToUpdate(store as Store)
        setIsUpdateDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: 'Eliminar marca',
      isFreeAction: false,
      onClick: (_: React.SyntheticEvent, store: Store | Store[]) => {
        setStoreToDelete(store as Store)
        setIsDeleteDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <DefaultTable
        title="Marcas"
        columns={columns}
        data={stores as StoreWithTags[]} // Especificamos el nuevo tipo con tags
        isLoading={isLoading}
        actions={actions}
      />
      {isCreateDialogOpen && (
        <CreateStoresDialog
          handleClose={() => setIsCreateDialogOpen(false)}
          onDone={onCreateDone}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteStoreDialog
          store={storeToDelete}
          handleClose={closeDeleteDialog}
          onDone={onDeleteDone}
        />
      )}
      {isUpdateDialogOpen && storeToUpdate && (
        <UpdateStoreDialog
          store={storeToUpdate}
          handleClose={closeUpdateDialog}
          onDone={onUpdateDone}
        />
      )}
    </>
  )
}

export { Stores }
